/**
 * How an automation is reached, read-only (D9 — Auslöser).
 *
 * ONLY WHAT THE DETAIL RESPONSE CARRIES. Three fields answer the question and
 * all three are shown: `trigger_type` (what starts it), `runtime_mode` (whether
 * it runs on its own, waits for a person, or — `preparing`, PC4 — does not run
 * yet) and `action_type` (what it does once started). `next_run_at` is shown when the server sends one — it does not today
 * (A3 answers `null` until a dispatcher exists), and a row of dashes would
 * advertise a schedule nobody can see.
 *
 * NO TIME OF DAY, AND THAT IS DELIBERATE. The birthday greeting goes out at
 * 09:00 Europe/Zurich, but no field of this API says so — the time lives in a
 * workflow file and a server gate (SoT §4.1 Nr. 7). Printing it here would be a
 * value the browser made up; the day the API carries a schedule, this tab shows
 * it. No editor either: nothing here can be changed, and a disabled control
 * would only promise that something can.
 *
 * THE SENTENCE IS DERIVED FROM `runtime_mode`, NOT FROM `enabled`. "Runs on its
 * own while switched on" is what `scheduled` means whatever the switch says
 * right now; whether it IS on is the header's statement, not this tab's.
 */
import React from "react";

import {
  AutomationIconTile,
  automationActionIcon,
  automationRuntimeModeIcon,
  automationTriggerIcon,
} from "./automationIcons";
import { formatDateTime } from "../../../utils/datetime";
import {
  automationActionLabel,
  automationRuntimeModeLabel,
  automationTriggerLabel,
} from "./automationLabels";

const RUNTIME_SENTENCES = {
  scheduled:
    "Läuft von selbst, solange die Automatisierung eingeschaltet ist.",
  manual_only: "Startet nur, wenn jemand sie von Hand auslöst.",
  preparing: "Läuft noch nicht. Die E-Mail-Vorlage kann bereits vorbereitet werden.",
};

function Field({ icon, label, children }) {
  return (
    <div className="flex items-start gap-2.5 rounded-[10px] border border-border bg-card px-3 py-2.5">
      <AutomationIconTile icon={icon} tone="muted" size="sm" />
      <div className="flex min-w-0 flex-col gap-0.5">
        <dt className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </dt>
        <dd className="m-0 text-[12.5px] text-foreground">{children}</dd>
      </div>
    </div>
  );
}

export default function AutomationTriggerTab({ automation }) {
  if (!automation) return null;
  const sentence = RUNTIME_SENTENCES[automation.runtime_mode];

  return (
    <div className="flex flex-col gap-3">
      {sentence ? (
        <p className="m-0 text-[12.5px] leading-relaxed text-muted-foreground">
          {sentence}
        </p>
      ) : null}

      <dl className="m-0 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Field icon={automationTriggerIcon(automation.trigger_type)} label="Auslöser">
          {automationTriggerLabel(automation.trigger_type)}
        </Field>
        <Field
          icon={automationRuntimeModeIcon(automation.runtime_mode)}
          label="Ausführungsmodus"
        >
          {automationRuntimeModeLabel(automation.runtime_mode)}
        </Field>
        <Field icon={automationActionIcon(automation.action_type)} label="Aktion">
          {automationActionLabel(automation.action_type)}
        </Field>
        {automation.next_run_at ? (
          <Field
            icon={automationTriggerIcon("schedule")}
            label="Nächste Ausführung"
          >
            {formatDateTime(automation.next_run_at)}
          </Field>
        ) : null}
      </dl>
    </div>
  );
}
